import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@mantine/core';
import { LineChart, BarChart } from '@mantine/charts';
import { useApiClient } from '../../api/ApiClientContext';
import {
  OrganisationInsightsResponse,
  KPIProgressData,
} from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import styles from './OrganisationInsights.module.scss';

const formatKpiValue = (value: number | null | undefined, unit?: string, precision: number = 0): string => {
  if (value == null) return '--';
  const formatted = precision > 0 ? value.toFixed(precision) : Math.round(value).toString();
  const unitLower = unit?.toLowerCase() || '';

  if (unitLower === '%' || unitLower === 'percent' || unitLower === 'percentage') {
    const pctVal = value * 100;
    const pctFormatted = precision > 0 ? pctVal.toFixed(precision) : Math.round(pctVal).toString();
    return `${pctFormatted}%`;
  }
  if (unitLower === '£' || unitLower === 'gbp') return `£${formatted}`;
  if (unitLower === '$' || unitLower === 'usd') return `$${formatted}`;
  if (unitLower === 'millions' || unitLower === 'million') return `${formatted}M`;
  return formatted;
};

const formatWeekLabel = (iso: string) => {
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}`;
};

const BEHAVIOURS_PER_PAGE = 5;

export const OrganisationInsights: React.FC = () => {
  const apiClient = useApiClient();
  const [selectedKpiId, setSelectedKpiId] = useState<string | null>(null);
  const [behaviourPage, setBehaviourPage] = useState(1);

  const { data, isLoading } = useQuery<OrganisationInsightsResponse>({
    queryKey: ['organisationInsights'],
    queryFn: async () => {
      const res = await apiClient.api.getOrganisationInsightsApiV1InsightsOrganisationGet();
      return res?.data;
    },
  });

  if (isLoading) return <div className={styles.loading}>Loading...</div>;

  const isEmpty = !data || !data.enabled_components || data.enabled_components.length === 0;
  if (isEmpty) {
    return (
      <div className={styles.root}>
        <div className={styles.titleRow}>
          <h1 className={styles.pageTitle}>Goals & KPIs</h1>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrapper}>
            <Icon iconName="GreyCalendarIcon" width="5rem" height="5rem" />
          </div>
          <h3 className={styles.emptyTitle}>No data available</h3>
          <p className={styles.emptyText}>
            Organisation insights are not available at the moment. Data will appear once initiatives and KPIs are configured.
          </p>
        </div>
      </div>
    );
  }

  const enabled = new Set(data.enabled_components);
  const counts = data.counts;
  const keyKpis = data.key_kpis ?? [];
  const allKpis = data.all_kpis ?? [];
  const kpiProgress = data.kpi_progress ?? [];
  const distribution = data.distribution ?? [];
  const activityLines = data.activity_over_time ?? [];
  const behaviours = data.behaviours ?? [];

  const activeKpiId = selectedKpiId ?? allKpis[0]?.id ?? null;
  const activeKpiData: KPIProgressData | undefined = kpiProgress.find((k) => k.kpi_id === activeKpiId);

  if (process.env.NODE_ENV === 'development') {
    console.log('[OrgInsights] allKpis:', allKpis.map(k => k.id));
    console.log('[OrgInsights] kpiProgress kpi_ids:', kpiProgress.map(k => k.kpi_id));
    console.log('[OrgInsights] activeKpiId:', activeKpiId, '→ match:', !!activeKpiData);
  }

  const kpiProgressChartData = activeKpiData?.weeks.map((w) => ({
    week: `W${w.week_number}`,
    progress: w.progress_pct,
  })) ?? [];

  const distributionChartData = distribution.map((d) => ({
    bucket: d.bucket === '4+' ? '4+' : `${d.bucket}`,
    percentage: d.percentage,
  }));

  const allWeeks = new Set<string>();
  activityLines.forEach((line) => line.weeks.forEach((w) => allWeeks.add(w.week_start)));
  const sortedWeeks = Array.from(allWeeks).sort();
  const activityChartData = sortedWeeks.map((weekStart) => {
    const row: Record<string, unknown> = { week: formatWeekLabel(weekStart) };
    activityLines.forEach((line) => {
      const wp = line.weeks.find((w) => w.week_start === weekStart);
      row[line.initiative_name] = wp?.avg_days_logged ?? 0;
    });
    return row;
  });

  const activitySeries = activityLines.map((line, i) => ({
    name: line.initiative_name,
    color: ['#4C6EF5', '#BE4BDB', '#12B886', '#FA5252', '#FD7E14'][i % 5],
  }));

  const kpiDropdownOptions = allKpis.map((k) => ({ value: k.id, label: k.title }));

  return (
    <div className={styles.root}>
      <div className={styles.titleRow}>
        <h1 className={styles.pageTitle}>Organisation Insights</h1>
      </div>

      {/* Counts */}
      {enabled.has('org_counts') && counts && (
        <div className={styles.countsBar}>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Initiatives</span>
            <span className={styles.countValue}>{counts.active_initiatives}</span>
          </div>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Behaviours</span>
            <span className={styles.countValue}>{counts.behaviours}</span>
          </div>
          <div className={styles.countItem}>
            <span className={styles.countLabel}>Total Users</span>
            <span className={styles.countValue}>{counts.total_users}</span>
          </div>
        </div>
      )}

      {/* Key KPIs */}
      {enabled.has('org_key_kpis') && keyKpis.length > 0 && (
        <div className={styles.kpiSection}>
          <span className={styles.kpiSectionLabel}>KEY KPIS</span>
          <div className={styles.kpiCards}>
            {keyKpis.map((kpi) => (
              <div key={kpi.id} className={styles.kpiCard}>
                <span className={styles.kpiTitle}>{kpi.title}</span>
                <div className={styles.kpiValues}>
                  <span className={styles.kpiCurrent}>{formatKpiValue(kpi.current_value, kpi.unit)}</span>
                  {kpi.target != null && (
                    <span className={styles.kpiTarget}>/ {formatKpiValue(kpi.target, kpi.unit)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Progress Chart */}
      {enabled.has('org_kpi_progress') && allKpis.length > 0 && kpiProgress.length > 0 && (
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <h3 className={styles.chartTitle}>KPI Progress Towards Target</h3>
            <Select
              data={kpiDropdownOptions}
              value={activeKpiId}
              onChange={(val) => setSelectedKpiId(val)}
              rightSection={<Icon iconName="ArrowDownIcon" width="0.75rem" height="0.75rem" />}
              classNames={{
                input: styles.selectInput,
                root: styles.selectRoot,
                wrapper: styles.selectWrapper,
              }}
              allowDeselect={false}
            />
          </div>
          {kpiProgressChartData.length > 0 ? (
            <LineChart
              h={280}
              data={kpiProgressChartData}
              dataKey="week"
              series={[{ name: 'progress', color: '#4C6EF5', label: 'Progress %' }]}
              curveType="linear"
              withDots
              gridAxis="y"
              yAxisProps={{ domain: [0, 100] }}
            />
          ) : (
            <div className={styles.emptyChart}>No progress data for this KPI</div>
          )}
        </div>
      )}

      {/* Charts Row */}
      <div className={styles.chartsRow}>
        {/* Distribution */}
        {enabled.has('org_distribution') && distribution.length > 0 && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Activity last 7 days</h3>
            <p className={styles.chartSubtitle}>Distribution of users by number of habits logged</p>
            <BarChart
              h={280}
              data={distributionChartData}
              dataKey="bucket"
              series={[{ name: 'percentage', color: '#4C6EF5' }]}
              gridAxis="y"
              yAxisProps={{ domain: [0, 100] }}
              barProps={{ radius: [4, 4, 0, 0] }}
              withTooltip={false}
            />
          </div>
        )}

        {/* Activity Over Time */}
        {enabled.has('org_activity_over_time') && activityLines.length > 0 && (
          <div className={styles.chartCard}>
            <h3 className={styles.chartTitle}>Activity over time</h3>
            <LineChart
              h={280}
              data={activityChartData}
              dataKey="week"
              series={activitySeries}
              curveType="linear"
              withDots
              gridAxis="y"
              withLegend
              legendProps={{ verticalAlign: 'top', height: 36 }}
            />
          </div>
        )}
      </div>

      {/* Behaviours Table */}
      {enabled.has('org_behaviours_table') && behaviours.length > 0 && (() => {
        const totalPages = Math.ceil(behaviours.length / BEHAVIOURS_PER_PAGE);
        const pagedBehaviours = behaviours.slice(
          (behaviourPage - 1) * BEHAVIOURS_PER_PAGE,
          behaviourPage * BEHAVIOURS_PER_PAGE,
        );

        const renderPageButtons = () => {
          const pages: (number | 'ellipsis')[] = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (behaviourPage > 3) pages.push('ellipsis');
            const start = Math.max(2, behaviourPage - 1);
            const end = Math.min(totalPages - 1, behaviourPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (behaviourPage < totalPages - 2) pages.push('ellipsis');
            pages.push(totalPages);
          }
          return pages;
        };

        return (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Behaviour</th>
                  <th>Avg. activity</th>
                  <th>Status</th>
                  <th>KPIs on target</th>
                  <th>The most improved KPI</th>
                  <th>Least improved KPI</th>
                </tr>
              </thead>
              <tbody>
                {pagedBehaviours.map((row) => (
                  <tr key={row.id}>
                    <td>{row.title}</td>
                    <td>{row.avg_activity} days per week</td>
                    <td>{row.status_label}</td>
                    <td>{row.kpis_on_target}/{row.kpis_total}</td>
                    <td>{row.most_improved_kpi?.title ?? '--'}</td>
                    <td>{row.least_improved_kpi?.title ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className={styles.pagination}>
                {renderPageButtons().map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`e${i}`} className={styles.pageEllipsis}>...</span>
                  ) : (
                    <button
                      key={p}
                      className={`${styles.pageButton} ${p === behaviourPage ? styles.pageActive : ''}`}
                      onClick={() => setBehaviourPage(p)}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};
